import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import DOMPurify from "dompurify";
import Auth from "../Auth/Auth";
import TicketSupportService, { BrowserInfo, TicketSupportData, TicketSupportError } from "../services/TicketSupportService";
import { ScreenStepType, MODAL_TITLE_ERROR, MODAL_BODY_ERROR } from "../components/TicketSupport/TicketSupportConstants";

export interface UseTicketSupportConfig
{
    component: string;
    screenName: ScreenStepType;
    ticketError?: TicketSupportError;
    formVal?: unknown;
    financialFormVal?: unknown;
    savingPlanId?: number | string;
    userIdentity?: string;
}

export interface UseTicketSupportReturn
{
    showSupportForm: boolean;
    setShowSupportForm: (show: boolean) => void;
    showResultModal: boolean;
    resultTitle: string;
    resultBody: string;
    showSuccessToast: boolean;
    successToastMessage: string;
    isSending: boolean;
    closeResultModal: () => void;
    closeSuccessToast: () => void;
    handleSend: (message: string, ticketType?: string) => Promise<void>;
}

type UserAgentData =
{
    platform?: string;
    brands?: { brand: string; version: string }[];
};

const getBrowserInfo = (): BrowserInfo =>
{
    const uaData = (navigator as Navigator & {userAgentData?: UserAgentData}).userAgentData;
    const platform = uaData?.platform || navigator.platform;
    const brands = uaData?.brands || [];

    let browserName = "Unknown";
    let browserVersion = "Unknown";

    if(brands.length)
    {
        const primaryBrand = brands.find((b) => b.brand !== "Not A Brand");
        if(primaryBrand)
        {
            browserName = primaryBrand.brand;
            browserVersion = primaryBrand.version;
        }
    }
    else
    {
        const detected = detectBrowserFromUA(navigator.userAgent);
        browserName = detected.name;
        browserVersion = detected.version;
    }

    return{
        browserName,
        browserVersion,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform,
    };
};

const detectBrowserFromUA = (ua: string): {name: string; version: string} =>
{
    const regexes =
    [
        { name: "Edge", regex: /Edg\/([\d.]+)/ },
        { name: "Chrome", regex: /Chrome\/([\d.]+)/ },
        { name: "Firefox", regex: /Firefox\/([\d.]+)/ },
        { name: "Safari", regex: /Version\/([\d.]+).*Safari/ },
        { name: "Opera", regex: /OPR\/([\d.]+)/ },
    ];

    for(const {name, regex} of regexes)
    {
        const match = ua.match(regex);
        if(match)
        {
            return { name, version: match[1] };
        }
    }

    return { name: "Unknown", version: "Unknown" };
};

/** Escala de la captura: menor = menos píxeles y menos tiempo bloqueando el hilo principal. */
const SCREENSHOT_SCALE = 0.85;

const getScreenshotTarget = (): HTMLElement =>
{
    return document.getElementById("root") ?? document.body;
};

/**
 * Dejamos que React pinte el modal y el navegador componga un frame antes de iniciar html2canvas.
 * Luego, si existe requestIdleCallback, arranca la captura cuando el hilo esté más libre
 * (timeout para no retrasar demasiado si la pestaña sigue ocupada).
 */
const scheduleScreenshotWork = (work: () => void): (() => void) =>
{
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() =>
    {
        raf2 = requestAnimationFrame(() =>
        {
            if(typeof window.requestIdleCallback === "function")
            {
                idleId = window.requestIdleCallback(() => work(), {timeout: 500});
            }
            else
            {
                timeoutId = window.setTimeout(work, 48);
            }
        });
    });

    return () =>
    {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        if(idleId !== undefined && typeof window.cancelIdleCallback === "function")
        {
            window.cancelIdleCallback(idleId);
        }
        if(timeoutId !== undefined)
        {
            clearTimeout(timeoutId);
        }
    };
};

const useTicketSupport = (config: UseTicketSupportConfig): UseTicketSupportReturn =>
{
    const { component, screenName, ticketError, formVal, financialFormVal, savingPlanId, userIdentity } = config;

    const [showSupportForm, setShowSupportForm] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultTitle, setResultTitle] = useState("");
    const [resultBody, setResultBody] = useState("");
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successToastMessage, setSuccessToastMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    // Ref para el screenshot: no provoca re-renders y se puede limpiar explícitamente
    const screenshotRef = useRef<string | null>(null);

    // Capturar screenshot en cuanto el usuario abre el formulario.
    // Si cancela antes de que termine, el cleanup cancela la escritura y libera la referencia.
    useEffect(() =>
    {
        if(!showSupportForm)
        {
            screenshotRef.current = null;
            return;
        }

        let cancelled = false;

        const cancelScheduled = scheduleScreenshotWork(() =>
        {
            if(cancelled)
            {
                return;
            }

            const target = getScreenshotTarget();

            // scale existe en html2canvas 1.4; @types/html2canvas 0.5 está desactualizado
            html2canvas(target,
            {
                scale: SCREENSHOT_SCALE,
                logging: false,
                useCORS: true,
                allowTaint: false,
            } as Parameters<typeof html2canvas>[1])
            .then((canvas) =>
            {
                if(!cancelled)
                {
                    screenshotRef.current = canvas.toDataURL("image/png");
                }
            })
            .catch(() =>
            {
                // Captura fallida: el ticket se enviará sin screenshot
            });
        });

        return () =>
        {
            cancelled = true;
            cancelScheduled();
            screenshotRef.current = null;
        };
    }, [showSupportForm]);

    const handleSend = async (message: string, ticketType?: string): Promise<void> =>
    {
        const user = Auth.getUserProfile();
        let hasError = false;

        const sanitizedMessage = DOMPurify.sanitize(message.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

        const ticketData: TicketSupportData =
        {
            userId: user?.id,
            savingPlanId: savingPlanId !== undefined ? Number(savingPlanId) : undefined,
            title: `[${screenName}] User: ${user?.id} - Componente: ${component}`,
            message: sanitizedMessage,
            ticketType: ticketType || undefined,
            browserInfo: getBrowserInfo(),
            screenshot: screenshotRef.current,
            screenName,
            path: window.location.pathname,
            timestamp: new Date().toISOString(),
            userIdentity,
            formVal: formVal !== undefined ? JSON.stringify(formVal) : undefined,
            financialFormVal: financialFormVal !== undefined ? JSON.stringify(financialFormVal) : undefined,
            ticketError: ticketError ?? {status: false, message: ""},
        };

        setIsSending(true);

        try
        {
            //TODO const response = await TicketSupportService.createNewSupportTicket(ticketData);
            const response = await TicketSupportService.createTicket(ticketData);

            if(response?.statusCode !== 204)
            {
                hasError = true;
                setResultTitle(MODAL_TITLE_ERROR);
                setResultBody(MODAL_BODY_ERROR);
            }
            else
            {
                setSuccessToastMessage("Consulta enviada correctamente.");
                setShowSuccessToast(true);
            }
        }
        catch(error)
        {
            hasError = true;
            console.error(`${MODAL_TITLE_ERROR}:`, error);
            setResultTitle(MODAL_TITLE_ERROR);
            setResultBody(MODAL_BODY_ERROR);
        }
        finally
        {
            setIsSending(false);
            screenshotRef.current = null;
            setShowSupportForm(false);
            setShowResultModal(hasError);
        }
    };

    const closeResultModal = () =>
    {
        setShowResultModal(false);
        setResultTitle("");
        setResultBody("");
    };

    const closeSuccessToast = () =>
    {
        setShowSuccessToast(false);
        setSuccessToastMessage("");
    };

    return{
        showSupportForm,
        setShowSupportForm,
        showResultModal,
        resultTitle,
        resultBody,
        showSuccessToast,
        successToastMessage,
        isSending,
        closeResultModal,
        closeSuccessToast,
        handleSend,
    };
};

export default useTicketSupport;